import React, { useCallback } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import styled from 'styled-components/';
import {
  Grid, Box, Radio, Chip, makeStyles
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { ReactComponent as ShoeShopN1 } from '../../../../medias/icon/shoe-shop-n1.svg';
import { ReactComponent as ShoeShopN1MY } from '../../../../medias_my/icon/shoe-shop-n1.svg';
import useResponsive from '../../../../utils/responsive';
import {
  Button, ButtonCardItem, Paragraph, Subheading,
} from '../../../common';
import { useLanguage } from '../../../../context/language/language';
import configuration from '../../../../configs/configuration';
import { useSite } from '../../../../context/siteContext';
import { getStoreOpenTime } from '../../../../utils/utils';
import useDeliveryData from '../../../common/hook/useDeliveryData';
import Display from '../../../common/Display';
import { getImageURL } from '../../../../utils/getImageURL';

const Driver = getImageURL('/icon/od_delivery_btn.svg');
const DeliveryMan = getImageURL('/icon/nd_delivery_btn.svg');
const ShoeShop = getImageURL('/icon/cc_delivery_btn.svg');

const lotusHyperImage = getImageURL('/images/lotus-hyper.svg');
const lotusGoFreshImage = getImageURL('/images/lotus-go-fresh.svg');

const Text = styled(Subheading)`
  font-size: 18px;
  line-height: 28px;
  color: ${({ theme, active }) =>
    (active ? theme.colors.green600 : theme.colors.grey700)};
`;
const Textlarge = styled(Subheading)`
  padding: ${({ theme }) => (theme.isTablet ? '0 0 0 16px' : '0 16px')};
  font-size: ${({ theme }) => '16px'};
  line-height: 28px;
  color: ${({ theme, active, disabled }) =>
    (disabled ? theme.colors.grey300 : theme.colors.grey700)};
`;
const TextDesc = styled(Paragraph)`
  font-size: 14px;
  padding-left: 16px;
  color: ${({ theme, active, disabled }) =>
    (disabled ? theme.colors.grey300 : theme.colors.grey550)};
`;

const TextHeadRadio = styled(Paragraph)(({
 theme, disabled, active, siteCode 
}) => ({
  ...theme.fonts.textL,
  lineHeight: siteCode === 'MY' ? '20px' : '24px',
  color: disabled ? theme.colors.grey500 : theme.colors.grey700,
  fontWeight: active ? 700 : 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
})); 

const TextDescRadio = styled(Paragraph)(({ theme, disabled }) => ({
  ...theme.fonts.textM,
  lineHeight: '18px',
  color: disabled ? theme.colors.grey300 : theme.colors.grey550,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
}));

const TextHeadSubButton = styled(Paragraph)(({ theme, disabled, active }) => ({
  ...theme.fonts.header6,
  fontSize: active ? theme.fonts.header6 : theme.fonts.textM,
  color: disabled ? theme.colors.grey500 : theme.colors.grey700,
  fontWeight: active ? 700 : 500,
}));

const TextDescSubButton = styled(Paragraph)(({ theme, disabled }) => ({
  ...theme.fonts.textM,
  color: disabled ? theme.colors.grey300 : theme.colors.grey550,
  lineHeight: '20px'
}));

const View = styled.div(({ theme }) => (
  [
    theme.isMobile ? {
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'center',
      alignItems: 'flex-start',
      textAlign: 'center',
      flexWrap: 'wrap'
    } : {
      justifyContent: 'center',
      alignItems: 'flex-start',
      textAlign: 'center',
    },
    {
      height: 130
    }
  ]));

const MobileButton = styled(ButtonCardItem)(({ theme, active, isWhite }) => ({
  backgroundColor: active ? theme.colors.green100 : theme.colors.white,
  border: active ? '1px solid #00BCB4' : `1px solid ${theme.colors.grey75}`,
  height: 'auto',
  boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.04)',
  cursor: 'pointer',
  ...theme.radius.m,
}));

const getBackgroundColor = (theme, active, disabled) => {
  if (active) {
    return theme.colors.green100;
  } 
  if (disabled) {
    return theme.colors.grey25;
  } 
  return theme.colors.white;
};

const getBorder = (theme, active, isWhite) => {
  if (active) {
    return '2px solid #00BCB4';
  } 
  if (isWhite) {
    return `1px solid ${theme.colors.grey75}`;
  } 
  return `1px solid ${theme.colors.grey100}`;
};

const MobileSubButton = styled(ButtonCardItem)(({
 theme, active, disabled, isWhite
}) => ({
    backgroundColor: getBackgroundColor(theme, active, disabled),
    border: getBorder(theme, active, isWhite),
    cursor: 'pointer',
    ...theme.radius.m,
    padding: active ? 8 : 10,
}));

const WebButton = styled(ButtonCardItem)(({
  theme, active, isWhite, disabled
}) => {
  const isWhiteDisabledBorderOrOther = isWhite || disabled ? `1px solid ${theme.colors.grey100}` : 'none';
  const isWhiteBgColor = isWhite ? theme.colors.white : theme.colors.grey25;
  const bgColor = disabled ? theme.colors.grey25 : isWhiteBgColor;
  return ({
    backgroundColor: active ? theme.colors.green100 : bgColor,
    border: active ? '1px solid #00BCB4' : isWhiteDisabledBorderOrOther,
    boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.04)',
    minWidth: theme.isTablet ? 190 : 240,
    ...theme.radius.l,

    '&:active': {
      backgroundColor: theme.colors.green100,
      border: '1px solid #00BCB4',
    },
    // eslint-disable-next-line no-dupe-keys
    cursor: 'pointer',
    // eslint-disable-next-line no-dupe-keys
    height: 100,
    boxSizing: 'border-box',
    paddingLeft: 16,

    '&:hover': {
      backgroundColor: theme.colors.grey25,
      border: '1px solid #E0E0E0',
    },
  });
});

const IKeyboardArrowRightIcon = styled(KeyboardArrowRightIcon)(
  ({ theme, active }) => ({
    color: active ? theme.colors.green600 : theme.colors.grey700,
    display: 'flex',
  }),
);

const Icon = styled.img({
  width: 56,
  height: 56,
});

const IconMobile = styled.img({
  width: 32,
  height: 32,
});

const useStyle = makeStyles((theme) => ({
  clip: {
    border: '1px solid rgba(249, 190, 0, 1)',
    borderRadius: 6,
    height: 18
  },
  label: {
    color: 'rgba(249, 190, 0, 1)',
    fontSize: '10px'
  },
  disabled: {
    opacity: 0.5
  },
  button: {
    [theme.breakpoints.only('md')]: {
      minWidth: '190px !important',
    }
  },
  chipCenter: {
    display: 'flex',
    alignItems: 'center'
  },
  radio: {
    color: '#A0A6A9',
    padding: 0,
    paddingRight: 8
  }

}));

const CustomImage = styled.img(
  ({ theme }) => ({
    width: theme.isMobile ? 44 : 56,
    height: theme.isMobile ? 44 : 56,
  })
);

const SubButtonIcon = styled.img({
  width: 40,
  height: 40,
  marginTop: 6,
  marginRight: 7
});

function RadioListButton(props) {
  const classes = useStyle();
  const { siteCode } = useSite();
  return (
    <Box>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="center"
        mb={props.isMargin ? '24px' : '16px'}
        onClick={() => props.onClick(props.value)}
      >
        <Box display="flex">
          <Radio
            checked={props.active || false}
            value={props.value}
            name={`radio-button-${props.value}`}
            inputProps={{
              'aria-label': props.value
            }}
            disabled={props.disabled}
            className={classes.radio}
          />
        </Box>
        <Box display="flex">
          <props.Icon
            alt={`icon-${props.value}`}
            className={clsx(props.disabled ? classes.disabled : '')}
          />
        </Box>
        <Box flex flexGrow="1" marginLeft={1}>
          <Box display="flex">
            <TextHeadRadio
              siteCode={siteCode}
              active={props.active || false}
              disabled={props.disabled}
            >
              {props.headText}
            </TextHeadRadio>
            {props.clip}
          </Box>
          <TextDescRadio
            siteCode={siteCode}
            active={props.active || false}
            disabled={props.disabled}
          >
            {props.descText}
          </TextDescRadio>
        </Box>
      </Box>
      <Display show={!_.isEmpty(props.subItems) && !props.disabled}>
        <Box display="flex" ml="32px" mb="24px">
          {props.subItems}
        </Box>
      </Display>
    </Box>
  );
}

function OnDemandStoreType(props) {
  const word = useLanguage();
  const deliveryData = useDeliveryData();
  const isNotSetDeliveryData = deliveryData.formData.delivery.deliveryType === null;
  let activeGoFresh;
  let activeHyper;
  if (isNotSetDeliveryData) {
    activeGoFresh = true;
    activeHyper = false;
  } else {
    activeGoFresh = props.selectState === 'OD' && props.selectStoreType === 'go_fresh';
    activeHyper = props.selectState === 'OD' && props.selectStoreType === 'hyper';
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={6}>
        <MobileSubButton
          isWhite
          active={props.selectState === 'OD' && props.selectStoreType === 'go_fresh'}
          disabled={deliveryData.disableGoFresh}
          height={40}
          leftIcon={<SubButtonIcon src={lotusGoFreshImage} />}
          content={(
            <>
              <TextHeadSubButton active={activeGoFresh} disabled={deliveryData.disableGoFresh}>
                {word('OnDemand.1hr')}
              </TextHeadSubButton>
              <TextDescSubButton active={activeGoFresh} disabled={deliveryData.disableGoFresh}>
                {word('OnDemand.GoFresh')}
              </TextDescSubButton>
            </>
          )}
          {...props}
          onClick={(event) => {
            event.stopPropagation();
            props.onClick('OD');
            props.setSelectStoreType('go_fresh');
          }}

        />
      </Grid>
      <Grid item xs={6}>
        <MobileSubButton
          isWhite
          active={props.selectState === 'OD' && props.selectStoreType === 'hyper'}
          disabled={deliveryData.disableHyper}
          height={40}
          leftIcon={<SubButtonIcon src={lotusHyperImage} />}
          content={(
            <>
              <TextHeadSubButton active={activeHyper} disabled={deliveryData.disableHyper}>
                {word('OnDemand.3hrs')}
              </TextHeadSubButton>
              <TextDescSubButton active={activeHyper} disabled={deliveryData.disableHyper}>
                {word('OnDemand.Hyper')}
              </TextDescSubButton>
            </>
          )}
          {...props}
          onClick={(event) => {
            event.stopPropagation();
            props.onClick('OD');
            props.setSelectStoreType('hyper');
          }}
        />
      </Grid>
    </Grid>
  );
}

export function HomeDeliveryButton(props) {
  const { isMobile } = useResponsive();
  const word = useLanguage();
  const { ThSite, MySite } = useSite();
  const currentLanguage = useSelector((state) => state.currentLanguage);
  const isTHLang = currentLanguage.language === 'th';
  const IconRadio = useCallback(() => <IconMobile src={DeliveryMan} alt="icon-HD" />, []);

  if (isMobile) {
    if (MySite) {
      return (
        <Grid xs={12}>
          <RadioListButton
            Icon={IconRadio}
            value="HD"
            active={props.active}
            headText={word('DeliveryAddress.Type.Options.HomeDelivery')}
            descText={word('DeliveryAddress.Type.Options.HomeDelivery.Description')}
            {...props}
          />
        </Grid>
      );
    }
    return (
      <Grid xs={12}>
        <RadioListButton
          Icon={IconRadio}
          value="HD"
          active={props.active}
          headText={word('DeliveryAddress.Type.Options.HomeDelivery')}
          isMargin
          {...props}
        />
      </Grid>
    );
  }
  return (
    <WebButton
      isWhite
      leftIcon={<Icon src={DeliveryMan} alt="icon-HD" />}
      content={<Textlarge active={props.active}>{word('DeliveryAddress.Type.Options.HomeDelivery')}</Textlarge>}
      desc={ThSite && isTHLang
        && <TextDesc active={props.active} disabled={props.disabled}>{word('DeliveryMethod.HomeDelivery.NextDay.2ndLine')}</TextDesc>}
      {...props}
    />
  );
}

export function ClickAndCollectButton(props) {
  const { isMobile } = useResponsive();
  const { MySite, ThSite } = useSite();
  const currentLanguage = useSelector((state) => state.currentLanguage);
  const isTHLang = currentLanguage.language === 'th';
  const word = useLanguage();
  const IconRadio = useCallback(() => <IconMobile src={ShoeShop} alt="icon-CC" />, []);

  if (isMobile) {
    if (MySite) {
      return (
        <Grid xs={12}>
          <RadioListButton
            Icon={IconRadio}
            value="CC"
            active={props.active}
            headText={word('DeliveryAddress.Type.Options.Collect')}
            {...props}
          />
        </Grid>
      );
    }
    return (
      <Grid xs={12}>
        <RadioListButton
          Icon={IconRadio}
          value="CC"
          active={props.active}
          headText={word('DeliveryAddress.Type.Options.Collect')}
          {...props}
        />
      </Grid>
    );
  }
  return (
    <WebButton
      isWhite
      leftIcon={<Icon src={ShoeShop} alt="icon-CC" />}
      content={<Textlarge active={props.active}>{word('DeliveryAddress.Type.Options.Collect')}</Textlarge>}
      desc={ThSite && isTHLang
        && <TextDesc active={props.active} disabled={props.disabled}>{word('DeliveryAddress.Type.Options.Collect.Description')}</TextDesc>}
      {...props}
    />
  );
}

export function ClickQuickAndCollectButton(props) {
  const { isMobile } = useResponsive();
  const warningStoreOpenTime = getStoreOpenTime();
  const classes = useStyle();
  const word = useLanguage();
  const { ThSite, MySite } = useSite();
  const currentLanguage = useSelector((state) => state.currentLanguage);
  const isTHLang = currentLanguage.language === 'th';
  const IconRadio = useCallback(() => <IconMobile src={Driver} alt="icon-OD" />, []);

  if (isMobile) {
    if (MySite) {
      return (
        <Grid xs={12}>
          <RadioListButton
            Icon={IconRadio}
            value="OD"
            active={props.active}
            headText={word('DeliveryAddress.Type.Options.OnDemand')}
            descText={word('DeliveryAddress.Type.Options.OnDemand.Description')}
            {...props}
            clip={warningStoreOpenTime.isClose && (
              <Box paddingX="8px" display="flex">
                <Chip
                  label={`${word('General.OpenHour.Closed')}`}
                  variant="outlined"
                  size="small"
                  classes={{
                    root: classes.clip,
                    label: classes.label,
                  }}
                />
              </Box>
            )}
          />
        </Grid>
      );
    }
    return (
      <Grid xs={12}>
        <RadioListButton
          Icon={IconRadio}
          value="OD"
          active={props.active}
          headText={word('DeliveryAddress.Type.Options.OnDemand')}
          {...props}
          clip={warningStoreOpenTime.isClose && (
            <Box paddingX="8px" display="flex" alignItems="center">
              <Chip
                label={`${word('General.OpenHour.Closed')}`}
                variant="outlined"
                size="small"
                classes={{
                  root: classes.clip,
                  label: classes.label,
                }}
              />
            </Box>
          )}
          subItems={(
            <OnDemandStoreType
              onClick={props.onClick}
              selectState={props.selectState}
              selectStoreType={props.selectStoreType}
              setSelectStoreType={props.setSelectStoreType}
            />
          )}
        />
      </Grid>
    );
  }
  return (
    <WebButton
      isWhite
      leftIcon={<Icon src={Driver} alt="icon-OD" />}
      content={(
        <Textlarge active={props.active} disabled={props.disabled}>
          {word('DeliveryMethod.OnDemand')}
        </Textlarge>
      )}
      desc={ThSite && isTHLang
        ? (
          <>
            {props.disabled
              ? (
                <Box paddingX="16px">
                  <Chip
                    label={`${word('General.OpenHour.Closed')}`}
                    variant="outlined"
                    size="small"
                    classes={{
                      root: classes.clip,
                      label: classes.label,
                    }}
                  />
                </Box>
              )
              : <TextDesc active={props.active} disabled={props.disabled}>{word('DeliveryMethod.OnDemand.2ndLine')}</TextDesc>}
          </>
        )
        : warningStoreOpenTime.isClose && (
          <Box paddingX="16px">
            <Chip
              label={`${word('General.OpenHour.Closed')}`}
              variant="outlined"
              size="small"
              classes={{
                root: classes.clip,
                label: classes.label,
              }}
            />
          </Box>
        )}
      {...props}
    />
  );
}

export function ClickPreOrderButton(props) {
  const { isMobile } = useResponsive();
  const word = useLanguage();
  const contentComponent = () => (
    <MobileButton
      isWhite
      content={(
        <View>
          <div>
            {configuration.siteCode === 'TH' ? <ShoeShopN1 /> : <ShoeShopN1MY />}
            <Text active={props.active}>{word('DeliveryMethod.ScheduleForLater')}</Text>
          </div>
        </View>
        )}
      {...props}
    />
  );

  if (isMobile) {
    return (
      <Button
        component={contentComponent}
      />
    );
  }

  return (
    <WebButton
      leftIcon={configuration.siteCode === 'TH' ? <ShoeShopN1 /> : <ShoeShopN1MY />}
      isWhite
      content={<Textlarge active={props.active}>{word('DeliveryMethod.ScheduleForLater')}</Textlarge>}
      {...props}
    />
  );
}

export function GoFreshButton(props) {
  const { isMobile } = useResponsive();
  const word = useLanguage();
  const IconRadio = useCallback(() => <CustomImage src={lotusGoFreshImage} alt="icon-go-fresh" />, []);

  if (isMobile) {
    return (
      <Grid xs={12}>
        <RadioListButton
          Icon={IconRadio}
          value="go_fresh"
          active={props.active}
          headText={word('DeliveryAddress.Type.Options.OnDemand.1hr')}
          descText={word('DeliveryAddress.Type.Options.OnDemand.1hr.Description')}
          {...props}
        />
      </Grid>
    );
  }
  return (
    <WebButton
      isWhite
      leftIcon={<CustomImage src={lotusGoFreshImage} alt="icon-go-fresh" />}
      content={<Textlarge active={props.active}>{word('DeliveryAddress.Type.Options.OnDemand.1hr')}</Textlarge>}
      desc={<TextDesc active={props.active} disabled={props.disabled}>{word('DeliveryAddress.Type.Options.OnDemand.1hr.Description')}</TextDesc>}
      {...props}
    />
  );
}

export function HyperButton(props) {
  const { isMobile } = useResponsive();
  const word = useLanguage();
  const IconRadio = useCallback(() => <CustomImage src={lotusHyperImage} alt="icon-hyper" />, []);

  if (isMobile) {
    return (
      <Grid xs={12}>
        <RadioListButton
          Icon={IconRadio}
          value="hyper"
          active={props.active}
          headText={word('DeliveryAddress.Type.Options.OnDemand.3hrs')}
          descText={word('DeliveryAddress.Type.Options.OnDemand.3hrs.Description')}
          {...props}
        />
      </Grid>
    );
  }
  return (
    <WebButton
      isWhite
      leftIcon={<CustomImage src={lotusHyperImage} alt="icon-hyper" />}
      content={<Textlarge active={props.active}>{word('DeliveryAddress.Type.Options.OnDemand.3hrs')}</Textlarge>}
      desc={<TextDesc active={props.active} disabled={props.disabled}>{word('DeliveryAddress.Type.Options.OnDemand.3hrs.Description')}</TextDesc>}
      {...props}
    />
  );
}

export default {};
